import { useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { useUIStore } from '@/stores/uiStore'
import { useIdentity } from '@/hooks/api'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { MobileDrawer } from './MobileDrawer'
import { cn } from '@/lib/utils'

interface AppLayoutProps {
  children?: React.ReactNode
}

/**
 * Main application layout with sidebar, header, and content area.
 * Responsive: shows mobile drawer on small screens, persistent sidebar on large screens.
 */
export function AppLayout({ children }: AppLayoutProps) {
  const sidebarState = useUIStore((state) => state.sidebarState)
  const initTheme = useUIStore((state) => state.initTheme)

  // Fetch user identity on mount - this populates the auth store with full user data
  useIdentity()

  // Initialize theme on mount
  useEffect(() => {
    initTheme()
  }, [initTheme])

  const isCollapsed = sidebarState === 'collapsed'

  return (
    <div className="flex min-h-screen bg-[var(--color-content-bg)] dark:bg-gray-950">
      {/* Desktop sidebar - hidden on mobile */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-30 hidden transition-all duration-300 lg:block',
          isCollapsed ? 'w-[72px]' : 'w-[240px]'
        )}
      >
        <Sidebar />
      </div>

      {/* Mobile drawer */}
      <MobileDrawer />

      {/*
        Main content area. Overflow is clipped rather than hidden: `hidden` makes
        these scroll containers, which would anchor any `sticky` page content to
        a box that never scrolls instead of to the viewport.
      */}
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col overflow-x-clip transition-all duration-300',
          'lg:ml-[240px]',
          isCollapsed && 'lg:ml-[72px]'
        )}
      >
        {/* Mobile header */}
        <Header />

        {/* Page content */}
        <main className="min-w-0 flex-1 overflow-clip p-4 lg:p-6">{children ?? <Outlet />}</main>
      </div>
    </div>
  )
}

export default AppLayout
